// LandingPage.tsx - Rallify Marketing Landing Page (signed-out)
//
// Layout follows the marketing wireframe:
//   Hero (copy + CTA + media)  ->  sales copy band  ->  four feature rows
//   (media + header + benefit) ->  "easy setup" CTA ->  time-savings value
//   slider -> footer.
//
// Every media slot renders a live in-app mock by default. To swap one for a
// real screen recording, drop the file into public/marketing/ and point the
// matching key in FEATURE_GIF / HERO_GIF at it.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_URL } from './config';
import { getWowClassColor } from './wowClassColor';
import { DISCORD_INVITE_URL, DISCORD_ICON_PATH } from './lib/links';

const loginHref = `${API_URL}/auth/discord`;

// Class icons come from the same Wowhead/zamimg CDN the app's game-option
// image_urls point at (see auth/discord_bot.js). Referenced statically here so the
// signed-out page needs no backend call. Falls back to a class-colored initial.
const CLASS_ICON: Record<string, string> = {
  Warrior: 'classicon_warrior', Paladin: 'classicon_paladin', Hunter: 'classicon_hunter',
  Rogue: 'classicon_rogue', Priest: 'classicon_priest', Shaman: 'classicon_shaman',
  Mage: 'classicon_mage', Warlock: 'classicon_warlock', Druid: 'classicon_druid',
  DeathKnight: 'classicon_deathknight', Monk: 'classicon_monk',
  DemonHunter: 'classicon_demonhunter', Evoker: 'classicon_evoker',
};
const classIconUrl = (cls: string) => {
  const slug = CLASS_ICON[cls.replace(/\s+/g, '')];
  return slug ? `https://wow.zamimg.com/images/wow/icons/large/${slug}.jpg` : null;
};

// Media overrides. Leave a slot undefined to keep its animated mock. A real
// asset here must exist in public/marketing/ or the browser shows a broken box.
const HERO_GIF: string | undefined = undefined;
type FeatureKey = 'roster' | 'loot' | 'attendance' | 'discord';
const FEATURE_GIF: Record<FeatureKey, string | undefined> = {
  roster: undefined,
  loot: undefined,
  attendance: undefined,
  discord: undefined,
};

const DiscordGlyph = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037 14.178 14.178 0 0 0-.624 1.29 18.423 18.423 0 0 0-4.825 0 14.12 14.12 0 0 0-.63-1.29.074.074 0 0 0-.077-.037 19.736 19.736 0 0 0-4.885 1.515.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.956 2.42-2.157 2.42zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.086 2.157 2.419 0 1.334-.946 2.42-2.157 2.42z"/></svg>
);

const DiscordButton = ({ label = 'Continue with Discord' }: { label?: string }) => (
  <a
    href={loginHref}
    className="group inline-flex items-center justify-center gap-3 rounded-xl bg-[#5865F2] hover:bg-[#4752C4] px-8 py-4 text-base font-bold text-white transition-all hover:scale-[1.02] shadow-lg shadow-[#5865F2]/20"
  >
    <DiscordGlyph className="h-5 w-5" />
    {label}
  </a>
);

// ----- Shared animation driver -------------------------------------------------
// Steps forward once per stepMs while the element is on screen, holds on the
// finished state for holdMs, then replays. Pauses entirely when scrolled away.
function useStepLoop(steps: number, stepMs = 460, holdMs = 2400) {
  const [n, setN] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const run = () => {
      clearTimers();
      setN(0);
      for (let i = 1; i <= steps; i++) {
        timers.current.push(window.setTimeout(() => setN(i), 300 + i * stepMs));
      }
      const total = 300 + steps * stepMs;
      timers.current.push(window.setTimeout(() => setN(0), total + holdMs));
      timers.current.push(window.setTimeout(run, total + holdMs + 400));
    };

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) run(); else clearTimers(); });
    }, { threshold: 0.25 });
    obs.observe(el);
    return () => { obs.disconnect(); clearTimers(); };
  }, [steps, stepMs, holdMs, clearTimers]);

  return { n, ref, done: n >= steps };
}

// Chrome shared by every mock so the media column reads as one product.
function MediaFrame({
  gif, title, children, innerRef,
}: {
  gif?: string;
  title: string;
  children: React.ReactNode;
  innerRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div ref={innerRef} className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-1 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-indigo-500/5 pointer-events-none" />
      <div className="relative rounded-xl bg-[#0d1117]/80 overflow-hidden">
        {/* fake window bar keeps the mock reading as a product screenshot */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5">
          <span className="w-2 h-2 rounded-full bg-white/15" />
          <span className="w-2 h-2 rounded-full bg-white/15" />
          <span className="w-2 h-2 rounded-full bg-white/15" />
          <span className="ml-2 text-[10px] font-medium text-white/30 truncate">{title}</span>
        </div>
        {gif ? (
          <img src={gif} alt={title} className="w-full block" />
        ) : (
          <div className="p-4 sm:p-5">{children}</div>
        )}
      </div>
    </div>
  );
}

// ----- Hero mock: self-assembling roster ---------------------------------------
type Role = 'Tank' | 'Healer' | 'DPS';
const ROSTER: { name: string; cls: string; role: Role }[] = [
  { name: 'Dawnguard',  cls: 'Paladin', role: 'Tank' },
  { name: 'Starfall',   cls: 'Druid',   role: 'DPS' },
  { name: 'Tidecaller', cls: 'Shaman',  role: 'DPS' },
  { name: 'Wavecrest',  cls: 'Shaman',  role: 'Healer' },
  { name: 'Ironhide',   cls: 'Warrior', role: 'DPS' },
  { name: 'Frostbite',  cls: 'Mage',    role: 'DPS' },
  { name: 'Barkhide',   cls: 'Druid',   role: 'DPS' },
  { name: 'Hexfire',    cls: 'Warlock', role: 'DPS' },
  { name: 'Swiftarrow', cls: 'Hunter',  role: 'DPS' },
  { name: 'Lightwell',  cls: 'Priest',  role: 'DPS' },
];

const ROLE_TEXT: Record<Role, string> = {
  Tank: 'text-sky-400',
  Healer: 'text-emerald-400',
  DPS: 'text-orange-400',
};

function ClassAvatar({ name, cls, size = 'w-7 h-7' }: { name: string; cls: string; size?: string }) {
  const color = getWowClassColor(cls);
  const icon = classIconUrl(cls);
  return (
    <span className={`relative ${size} rounded-md shrink-0 overflow-hidden`} style={{ border: `1px solid ${color}66` }}>
      {icon && (
        <img
          src={icon}
          alt={cls}
          className="w-full h-full object-cover"
          onError={(e) => {
            const el = e.currentTarget;
            el.style.display = 'none';
            const fb = el.nextElementSibling as HTMLElement | null;
            if (fb) fb.style.display = 'flex';
          }}
        />
      )}
      <span
        className="absolute inset-0 items-center justify-center text-[11px] font-black"
        style={{ display: icon ? 'none' : 'flex', backgroundColor: `${color}22`, color }}
      >
        {name[0]}
      </span>
    </span>
  );
}

function RosterCard() {
  const { n: filled, ref, done } = useStepLoop(ROSTER.length, 80, 2600);

  return (
    <MediaFrame innerRef={ref} gif={HERO_GIF} title="Rallify · Raid planner">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-white truncate">Karazhan · Friday 8:00 PM</h3>
            <p className="text-[11px] text-white/40">1 Tank · 1 Healer · 8 DPS</p>
          </div>
        </div>
        <span className={`shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
          done ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300' : 'bg-white/5 border-white/10 text-white/60'
        }`}>
          {done ? '✓ Raid ready' : `${filled}/${ROSTER.length}`}
        </span>
      </div>

      {/* Slots */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {ROSTER.map((p, i) => {
          const isFilled = i < filled;
          const color = getWowClassColor(p.cls);
          return (
            <div key={i} className={`flex items-center gap-2.5 h-12 px-2.5 rounded-lg border transition-all duration-300 ${
              isFilled ? 'bg-white/[0.04] border-white/10 opacity-100 translate-y-0' : 'bg-transparent border-dashed border-white/10 opacity-60 translate-y-1'
            }`}>
              {isFilled ? (
                <>
                  <ClassAvatar name={p.name} cls={p.cls} />
                  <span className="text-xs font-bold truncate" style={{ color }}>{p.name}</span>
                  <span className={`text-[10px] font-bold ml-auto shrink-0 ${ROLE_TEXT[p.role]}`}>{p.role}</span>
                </>
              ) : (
                <>
                  <span className="w-7 h-7 rounded-md border border-dashed border-white/15 shrink-0" />
                  <span className="text-[11px] text-white/25">{p.role}</span>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Comp summary - lights up when full */}
      <div className={`flex items-center gap-2 mt-4 pt-3 border-t border-white/5 transition-opacity duration-500 ${done ? 'opacity-100' : 'opacity-30'}`}>
        <svg className={`w-4 h-4 ${done ? 'text-emerald-400' : 'text-white/30'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        <span className="text-[11px] font-medium text-white/60">1 Tank · 1 Healer · 8 DPS</span>
      </div>
    </MediaFrame>
  );
}

// ----- Feature mock 1: availability -> scheduled night -------------------------
const DAY_LABELS = ['W', 'T', 'F', 'S', 'S', 'M', 'T'];
// Per-raider availability across the 7 reset days, 1 = free. The mock reveals
// one raider per step, then highlights the column the solver would pick.
const AVAIL_ROWS: { name: string; cls: string; slots: number[] }[] = [
  { name: 'Dawnguard',  cls: 'Paladin', slots: [0, 1, 1, 1, 0, 1, 0] },
  { name: 'Wavecrest',  cls: 'Shaman',  slots: [1, 0, 1, 1, 1, 0, 0] },
  { name: 'Frostbite',  cls: 'Mage',    slots: [0, 1, 1, 0, 1, 1, 0] },
  { name: 'Hexfire',    cls: 'Warlock', slots: [1, 1, 1, 1, 0, 0, 1] },
  { name: 'Swiftarrow', cls: 'Hunter',  slots: [0, 0, 1, 1, 1, 0, 1] },
];
const BEST_DAY = 2; // Friday: the only column everyone shares

function RosterPlanningMock() {
  const { n, ref, done } = useStepLoop(AVAIL_ROWS.length);
  return (
    <MediaFrame innerRef={ref} gif={FEATURE_GIF.roster} title="Rallify · Availability">
      <div className="space-y-2">
        <div className="grid grid-cols-[84px_repeat(7,1fr)] gap-1.5 items-center">
          <span className="text-[10px] uppercase tracking-wider text-white/25">Raider</span>
          {DAY_LABELS.map((d, i) => (
            <span key={i} className={`text-[10px] text-center font-bold ${done && i === BEST_DAY ? 'text-emerald-300' : 'text-white/25'}`}>{d}</span>
          ))}
        </div>
        {AVAIL_ROWS.map((r, ri) => {
          const shown = ri < n;
          return (
            <div key={r.name} className={`grid grid-cols-[84px_repeat(7,1fr)] gap-1.5 items-center transition-all duration-300 ${shown ? 'opacity-100 translate-y-0' : 'opacity-25 translate-y-1'}`}>
              <span className="text-[11px] font-bold truncate" style={{ color: getWowClassColor(r.cls) }}>{r.name}</span>
              {r.slots.map((s, si) => (
                <span
                  key={si}
                  className={`h-6 rounded transition-all duration-300 ${
                    !shown ? 'bg-white/[0.03] border border-dashed border-white/10'
                      : s ? (done && si === BEST_DAY ? 'bg-emerald-400/70' : 'bg-cyan-400/30')
                        : 'bg-white/[0.04]'
                  }`}
                />
              ))}
            </div>
          );
        })}
      </div>
      <div className={`mt-4 flex items-center gap-2 rounded-lg border px-3 py-2.5 transition-all duration-500 ${
        done ? 'border-emerald-500/30 bg-emerald-500/10 opacity-100' : 'border-white/10 bg-white/[0.02] opacity-40'
      }`}>
        <svg className={`w-4 h-4 shrink-0 ${done ? 'text-emerald-400' : 'text-white/30'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
        <span className="text-[11px] font-bold text-white/80">
          {done ? 'Friday 8:00 PM · everyone available · roster built' : 'Reading availability…'}
        </span>
      </div>
    </MediaFrame>
  );
}

// ----- Feature mock 2: loot ledger ---------------------------------------------
const LOOT_ROWS: { item: string; quality: string; winner: string; cls: string; note: string }[] = [
  { item: "Netherstrand Longbow", quality: '#a335ee', winner: 'Swiftarrow', cls: 'Hunter',  note: 'Softres' },
  { item: "Cataclysm's Edge",     quality: '#a335ee', winner: 'Ironhide',   cls: 'Warrior', note: 'Council' },
  { item: 'Shroud of Absolution', quality: '#a335ee', winner: 'Lightwell',  cls: 'Priest',  note: 'BiS' },
  { item: 'Fang of the Leviathan',quality: '#a335ee', winner: 'Hexfire',    cls: 'Warlock', note: 'Offspec' },
];

function LootMock() {
  const { n, ref, done } = useStepLoop(LOOT_ROWS.length, 520);
  return (
    <MediaFrame innerRef={ref} gif={FEATURE_GIF.loot} title="Rallify · Loot ledger">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-bold text-white/60">Karazhan · this week</span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-colors ${
          done ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-300' : 'border-white/10 bg-white/5 text-white/40'
        }`}>{n}/{LOOT_ROWS.length} awarded</span>
      </div>
      <div className="space-y-2">
        {LOOT_ROWS.map((l, i) => {
          const shown = i < n;
          return (
            <div key={l.item} className={`flex items-center gap-2.5 rounded-lg border px-2.5 py-2 transition-all duration-300 ${
              shown ? 'border-white/10 bg-white/[0.04] opacity-100 translate-x-0' : 'border-dashed border-white/10 bg-transparent opacity-30 -translate-x-1'
            }`}>
              <span className="w-7 h-7 rounded-md shrink-0" style={{ backgroundColor: `${l.quality}22`, border: `1px solid ${l.quality}66` }} />
              <span className="text-[11px] font-bold truncate" style={{ color: l.quality }}>{l.item}</span>
              <span className="ml-auto flex items-center gap-1.5 shrink-0">
                <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${shown ? 'bg-white/5 text-white/40' : 'text-transparent'}`}>{l.note}</span>
                <span className="text-[11px] font-bold" style={{ color: getWowClassColor(l.cls) }}>{shown ? l.winner : ''}</span>
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-3 border-t border-white/5 flex items-center gap-2">
        <svg className={`w-4 h-4 ${done ? 'text-emerald-400' : 'text-white/25'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
        <span className="text-[11px] text-white/50">Every drop logged, priority history kept forever.</span>
      </div>
    </MediaFrame>
  );
}

// ----- Feature mock 3: attendance & reliability --------------------------------
const ATTENDANCE: { name: string; cls: string; pct: number; streak: string }[] = [
  { name: 'Dawnguard',  cls: 'Paladin', pct: 96, streak: '12 in a row' },
  { name: 'Wavecrest',  cls: 'Shaman',  pct: 88, streak: '7 in a row' },
  { name: 'Frostbite',  cls: 'Mage',    pct: 74, streak: '2 no-shows' },
  { name: 'Hexfire',    cls: 'Warlock', pct: 61, streak: 'Slipping' },
];

function AttendanceMock() {
  const { n, ref } = useStepLoop(ATTENDANCE.length, 420);
  return (
    <MediaFrame innerRef={ref} gif={FEATURE_GIF.attendance} title="Rallify · Attendance">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[11px] font-bold text-white/60">Last 12 raids</span>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-white/10 bg-white/5 text-white/40">Guild avg 80%</span>
      </div>
      <div className="space-y-3">
        {ATTENDANCE.map((a, i) => {
          const shown = i < n;
          const bar = a.pct >= 85 ? 'bg-emerald-400' : a.pct >= 70 ? 'bg-amber-400' : 'bg-red-400';
          const label = a.pct >= 85 ? 'text-emerald-300' : a.pct >= 70 ? 'text-amber-300' : 'text-red-300';
          return (
            <div key={a.name} className="flex items-center gap-2.5">
              <ClassAvatar name={a.name} cls={a.cls} size="w-6 h-6" />
              <span className="text-[11px] font-bold w-20 truncate" style={{ color: getWowClassColor(a.cls) }}>{a.name}</span>
              <span className="flex-1 h-2 rounded-full bg-white/[0.06] overflow-hidden">
                <span
                  className={`block h-full rounded-full ${bar} transition-all duration-700 ease-out`}
                  style={{ width: shown ? `${a.pct}%` : '0%' }}
                />
              </span>
              <span className={`text-[11px] font-black w-9 text-right ${shown ? label : 'text-transparent'}`}>{a.pct}%</span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 pt-3 border-t border-white/5 flex items-center gap-2">
        <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
        <span className="text-[11px] text-white/50">Know who shows up before you build the roster.</span>
      </div>
    </MediaFrame>
  );
}

// ----- Feature mock 4: Discord integration -------------------------------------
const DISCORD_SIGNUPS: { name: string; cls: string }[] = [
  { name: 'Dawnguard',  cls: 'Paladin' },
  { name: 'Wavecrest',  cls: 'Shaman' },
  { name: 'Frostbite',  cls: 'Mage' },
  { name: 'Swiftarrow', cls: 'Hunter' },
  { name: 'Lightwell',  cls: 'Priest' },
  { name: 'Hexfire',    cls: 'Warlock' },
];

function DiscordMock() {
  const { n, ref, done } = useStepLoop(DISCORD_SIGNUPS.length, 380);
  return (
    <MediaFrame innerRef={ref} gif={FEATURE_GIF.discord} title="Discord · #raid-signups">
      <div className="flex items-start gap-2.5">
        <span className="w-8 h-8 rounded-full bg-[#5865F2]/20 border border-[#5865F2]/40 flex items-center justify-center shrink-0">
          <DiscordGlyph className="w-4 h-4 text-[#8b94f7]" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-white">Rallify</span>
            <span className="text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-[#5865F2] text-white">Bot</span>
            <span className="text-[10px] text-white/25">Today at 19:04</span>
          </div>
          <div className="mt-1.5 rounded-lg border-l-2 border-l-cyan-400 bg-white/[0.03] px-3 py-2.5">
            <div className="text-[12px] font-bold text-white">Karazhan · Friday 8:00 PM</div>
            <div className="text-[11px] text-white/40 mt-0.5">React to sign up. Roster locks Thursday.</div>
            <div className="flex flex-wrap gap-1.5 mt-2.5 min-h-[28px]">
              {DISCORD_SIGNUPS.map((s, i) => (
                <span
                  key={s.name}
                  className={`inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 pl-1 pr-2 py-0.5 transition-all duration-300 ${
                    i < n ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                  }`}
                >
                  <ClassAvatar name={s.name} cls={s.cls} size="w-4 h-4" />
                  <span className="text-[10px] font-bold" style={{ color: getWowClassColor(s.cls) }}>{s.name}</span>
                </span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="inline-flex items-center gap-1 text-[11px] rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2 py-0.5 text-cyan-200 font-bold">
              ✅ {n}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-white/40 font-bold">
              ❓ 1
            </span>
            <span className={`text-[10px] font-bold transition-opacity duration-500 ${done ? 'opacity-100 text-emerald-300' : 'opacity-0'}`}>
              Synced to Rallify
            </span>
          </div>
        </div>
      </div>
    </MediaFrame>
  );
}

// ----- Feature rows ------------------------------------------------------------
const FEATURES: {
  key: FeatureKey;
  header: string;
  benefit: string;
  bullets: string[];
  media: React.ReactNode;
}[] = [
  {
    key: 'roster',
    header: 'Raid roster planning',
    benefit: 'Everyone sets their time once. Rallify reads the whole guild\'s availability, respects lockouts and alts, and builds a balanced roster in seconds.',
    bullets: ['Auto-balanced tanks, healers and DPS', 'Lockout and alt aware', 'Drag to tweak, commit when happy'],
    media: <RosterPlanningMock />,
  },
  {
    key: 'loot',
    header: 'Loot management',
    benefit: 'Soft reserves, loot council and full drop history in one ledger. No more spreadsheets, no more "who got that last time?"',
    bullets: ['Soft reserve sheets per raid', 'Council votes with BiS context', 'Permanent award history'],
    media: <LootMock />,
  },
  {
    key: 'attendance',
    header: 'Attendance & reliability',
    benefit: 'See who actually shows up. Attendance, no-shows and streaks are tracked automatically from every raid you run.',
    bullets: ['Attendance % per raider', 'No-show and streak tracking', 'Bench fairly with real numbers'],
    media: <AttendanceMock />,
  },
  {
    key: 'discord',
    header: 'Discord integration',
    benefit: 'Your guild already lives in Discord. Signups, reminders and posted rosters happen there, and sync straight back into Rallify.',
    bullets: ['Signups from a Discord post', 'Automatic reminders', 'Sign in with Discord, no new account'],
    media: <DiscordMock />,
  },
];

function FeatureRow({ feature, index }: { feature: typeof FEATURES[number]; index: number }) {
  // Media stays left on desktop like the wireframe; the copy column flips order
  // on mobile so the header always reads before its screenshot.
  return (
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center py-12 border-t border-white/5">
      <div className="lg:order-1 order-2">{feature.media}</div>
      <div className="lg:order-2 order-1">
        <span className="text-[11px] font-black uppercase tracking-[0.2em] text-cyan-400/70">
          {String(index + 1).padStart(2, '0')}
        </span>
        <h3 className="text-2xl sm:text-3xl font-black text-white mt-2 mb-3">{feature.header}</h3>
        <p className="text-base text-white/50 leading-relaxed mb-5">{feature.benefit}</p>
        <ul className="space-y-2.5">
          {feature.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5">
              <svg className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
              <span className="text-sm text-white/60">{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ----- Value slider: how much time the guild burns today -----------------------
// Each chore has an hours/week slider; `saved` is the share Rallify takes off
// the officer's plate, so the headline number reacts as they drag.
const CHORES: { id: string; label: string; max: number; start: number; saved: number }[] = [
  { id: 'signups',      label: 'Chasing signups',      max: 10, start: 3, saved: 0.9 },
  { id: 'rosters',      label: 'Building rosters',     max: 10, start: 1, saved: 0.95 },
  { id: 'loot',         label: 'Managing loot',        max: 10, start: 2, saved: 0.8 },
  { id: 'replacements', label: 'Finding replacements', max: 10, start: 5, saved: 0.85 },
];

function ValueSlider() {
  const [hours, setHours] = useState<Record<string, number>>(() =>
    Object.fromEntries(CHORES.map((c) => [c.id, c.start]))
  );

  const { spent, saved } = useMemo(() => {
    let spentTotal = 0;
    let savedTotal = 0;
    for (const c of CHORES) {
      const h = hours[c.id] ?? 0;
      spentTotal += h;
      savedTotal += h * c.saved;
    }
    return { spent: spentTotal, saved: savedTotal };
  }, [hours]);

  const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  const unit = (n: number) => (n === 1 ? 'hr' : 'hrs');
  const half = (n: number) => Math.round(n * 2) / 2;

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-8 items-start">
      {/* Sliders */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8">
        <p className="text-sm font-bold text-white/70 mb-6">How much time do you spend on:</p>
        <div className="space-y-6">
          {CHORES.map((c, i) => {
            const value = hours[c.id] ?? 0;
            return (
              <div key={c.id}>
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <label htmlFor={`chore-${c.id}`} className="text-sm font-bold text-white">
                    <span className="text-white/30 mr-1.5">{i + 1}.</span>{c.label}
                  </label>
                  <span className="text-sm font-black text-amber-400 tabular-nums shrink-0">{fmt(value)} {unit(value)}/wk</span>
                </div>
                <input
                  id={`chore-${c.id}`}
                  type="range"
                  min={0}
                  max={c.max}
                  step={0.5}
                  value={value}
                  onChange={(e) => setHours((h) => ({ ...h, [c.id]: Number(e.target.value) }))}
                  aria-label={`Hours per week spent ${c.label.toLowerCase()}`}
                  // appearance-none strips the native track AND thumb, so both
                  // are drawn here: track via the inline gradient (which doubles
                  // as the amber fill), thumb via the pseudo-element rules.
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                    [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400
                    [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#0d1117]
                    [&::-webkit-slider-thumb]:shadow-[0_0_0_3px_rgba(245,158,11,0.25)] [&::-webkit-slider-thumb]:cursor-pointer
                    [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full
                    [&::-moz-range-thumb]:bg-amber-400 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#0d1117]
                    [&::-moz-range-thumb]:cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${(value / c.max) * 100}%, rgba(255,255,255,0.1) ${(value / c.max) * 100}%, rgba(255,255,255,0.1) 100%)`,
                    accentColor: '#F59E0B',
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Result */}
      <div className="relative rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.12] to-transparent p-6 sm:p-8 text-center lg:sticky lg:top-6">
        <p className="text-[11px] font-black uppercase tracking-[0.2em] text-amber-400/70">Rallify saves you</p>
        <div className="mt-2 flex items-baseline justify-center gap-2">
          <span className="text-6xl font-black text-amber-400 tabular-nums leading-none">{fmt(half(saved))}</span>
          <span className="text-base font-bold text-white/50">{unit(half(saved))} / week</span>
        </div>
        <p className="mt-3 text-sm text-white/50 leading-relaxed">
          That is about <span className="font-bold text-white">{Math.round(saved * 52)} hours a year</span> back
          in your raid nights instead of your spreadsheets.
        </p>
        <div className="mt-5 pt-5 border-t border-white/10 space-y-2 text-left">
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/40">Officer admin today</span>
            <span className="font-bold text-white/60 line-through decoration-red-400/50 decoration-2">{fmt(spent)} {unit(spent)}/wk</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/40">With Rallify</span>
            <span className="font-black text-emerald-300">{fmt(half(spent - saved))} {unit(half(spent - saved))}/wk</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage({ onTryIt }: { onTryIt?: () => void }) {
  // Launch waitlist: while the flag is on, the no-account trial is replaced by
  // a "Join the waitlist" CTA (cold signups get waitlisted after Discord auth).
  const [waitlistMode, setWaitlistMode] = useState(false);
  useEffect(() => {
    fetch("/waitlist/config")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) setWaitlistMode(d.waitlistMode === true);
      })
      .catch(() => {});
  }, []);

  // Funnel: count each visitor once per browser session. anon_id dedupes
  // repeat visits from the same browser in the admin funnel.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("lfm_hp_tracked")) return;
      sessionStorage.setItem("lfm_hp_tracked", "1");
      let anonId = localStorage.getItem("lfm_anon_id");
      if (!anonId) {
        anonId = Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem("lfm_anon_id", anonId);
      }
      fetch(`${API_URL}/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event: "homepage_visited", anon_id: anonId }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Storage unavailable (private mode etc): skip tracking silently.
    }
  }, []);

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative px-6 pt-12 pb-14">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-gradient-to-br from-cyan-500/15 via-indigo-500/10 to-transparent rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto">
          {/* Copy + CTA */}
          <div className="text-center">
            <div className="mb-6">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-cyan-500" />
                </span>
                <span className="text-[11px] font-medium text-white/60">Now supporting WoW Classic & TBC</span>
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white tracking-tight leading-[1.1] mb-5">
              Less admin.{" "}
              {/* Solid fill, not background-clip:text. The clipped-gradient
                  version renders invisible on some Windows/Chrome setups. */}
              <span className="text-cyan-300">More play.</span>
            </h1>

            <p className="text-base sm:text-lg text-white/50 max-w-xl mx-auto leading-relaxed mb-8">
              Everyone sets their time once. Rallify builds a complete, balanced raid in seconds.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {waitlistMode ? (
                <a
                  href={loginHref}
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 px-8 py-4 text-base font-bold text-white transition-all hover:scale-[1.02] shadow-lg shadow-amber-500/20"
                >
                  Join the waitlist
                  <svg className="h-4 w-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                </a>
              ) : onTryIt ? (
                <button
                  onClick={onTryIt}
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 hover:from-cyan-400 hover:to-indigo-400 px-8 py-4 text-base font-bold text-white transition-all hover:scale-[1.02] shadow-lg shadow-cyan-500/20"
                >
                  Try it free
                  <svg className="h-4 w-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                </button>
              ) : null}
              <DiscordButton />
              {/* Additive only: the waitlist / try-it / login flow above is
                  untouched. Same metrics as the two buttons beside it (px-8 py-4,
                  text-base, font-bold) and an inset ring rather than a border, so
                  all three measure 56px. Outlined rather than filled because it
                  sits next to the blurple LOGIN button and two solid blurple
                  buttons would read as the same action. */}
              <a
                href={DISCORD_INVITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 rounded-xl ring-1 ring-inset ring-[#5865F2]/50 hover:ring-[#5865F2]/80 bg-[#5865F2]/10 hover:bg-[#5865F2]/20 px-8 py-4 text-base font-bold text-white/75 hover:text-white transition-all"
              >
                <DiscordGlyph className="h-5 w-5 text-[#8891f2]" />
                Join our Discord
              </a>
            </div>
            <p className="mt-4 text-xs text-white/30 text-center">
              {waitlistMode
                ? "Invite-only launch · Free to use · Secure Discord login"
                : "No signup to try · Free to use · Secure Discord login"}
            </p>
          </div>

          {/* Hero centerpiece: the time the guild burns today, live */}
          <div className="mt-14">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-black text-white mb-2">The hours you get back</h2>
              <p className="text-white/50 text-sm max-w-lg mx-auto">
                Drag the sliders to your guild's reality and see what running it costs you today.
              </p>
            </div>
            <ValueSlider />
          </div>
        </div>
      </section>

      {/* SALES COPY BAND + roster media */}
      {/* Shares the features grid (max-w-6xl, media left) so this card lines up
          with the four feature mocks below it. */}
      <section className="relative px-6 pt-4 pb-4">
        <div className="max-w-6xl mx-auto border-t border-white/5 pt-14">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white leading-snug max-w-3xl mx-auto text-center">
            Rallify lets you run your guild the easy way.
          </h2>
          <div className="mt-10 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="lg:order-1 order-2"><RosterCard /></div>
            <div className="lg:order-2 order-1">
              <p className="text-base sm:text-lg text-white/50 leading-relaxed">
                Scheduling, loot, attendance and Discord, all in one place. Officers stop doing
                unpaid admin work and the guild just shows up and raids.
              </p>
              <p className="mt-4 text-base text-white/50 leading-relaxed">
                One click turns a week of everyone's availability into a full, balanced roster,
                posted and ready to go.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative px-6 py-10">
        <div className="max-w-6xl mx-auto">
          {FEATURES.map((f, i) => (
            <FeatureRow key={f.key} feature={f} index={i} />
          ))}
        </div>
      </section>

      {/* SETUP CTA */}
      <section className="relative py-20 px-6 border-t border-white/5">
        <div className="max-w-2xl mx-auto text-center">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[200px] bg-gradient-to-r from-cyan-500/15 to-indigo-500/15 rounded-full blur-[80px] pointer-events-none" />
          <div className="relative z-10">
            <h2 className="text-3xl sm:text-5xl font-black text-white mb-4">Easy setup in 5 minutes</h2>
            <p className="text-sm text-white/40 mb-8 max-w-md mx-auto">
              Sign in with Discord, bring your roster over, run your next raid. No install, no card.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-3">
              {waitlistMode ? (
                <a
                  href={loginHref}
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 px-8 py-4 text-base font-bold text-white transition-all hover:scale-[1.02] shadow-lg shadow-amber-500/20"
                >
                  Join the waitlist
                  <svg className="h-4 w-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                </a>
              ) : null}
              <DiscordButton />
            </div>
          </div>
        </div>
      </section>

      {/* RALLIFY COMMUNITY DISCORD.
          Deliberately worded as the Rallify community server, not a guild's own
          Discord. Every other Discord reference on this page is "sign in with
          Discord" or a guild's server, so an unqualified invite here would read
          as one of those. */}
      <section className="relative py-16 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl border border-indigo-500/25 bg-gradient-to-br from-indigo-500/10 to-violet-500/5 px-6 py-8 sm:px-10">
            <div className="absolute -top-16 -right-10 h-40 w-40 rounded-full bg-indigo-500/20 blur-[70px] pointer-events-none" />
            <div className="relative z-10 flex flex-col sm:flex-row sm:items-center gap-6">
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl sm:text-3xl font-black text-white mb-2">
                  Join the Rallify community
                </h2>
                <p className="text-sm text-white/50 leading-relaxed">
                  Our official Discord, not a guild server. Ask questions, get help setting up,
                  suggest features and hear first when something ships.
                </p>
              </div>
              <a
                href={DISCORD_INVITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex shrink-0 items-center justify-center gap-2.5 rounded-xl bg-[#5865F2] hover:bg-[#4752C4] px-7 py-4 text-base font-bold text-white transition-all hover:scale-[1.02] shadow-lg shadow-indigo-500/25"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d={DISCORD_ICON_PATH} />
                </svg>
                Join our Discord
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative py-10 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-cyan-500/20 to-indigo-500/20 border border-cyan-500/30 flex items-center justify-center">
              <span className="text-xs font-bold text-cyan-300">R</span>
            </div>
            <span className="text-xs text-white/40">Rallify · Less admin. More play.</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-white/30">
            <a
              href={DISCORD_INVITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d={DISCORD_ICON_PATH} />
              </svg>
              Discord
            </a>
            <a href="#" className="hover:text-white transition-colors">Help</a>
            <a href="#" className="hover:text-white transition-colors">Terms</a>
            <a href="#" className="hover:text-white transition-colors">Privacy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
